import {
  OWNER_JARVIS_CONFIRMATION,
  OWNER_JARVIS_OPERATION_KIND,
  defineOwnerJarvisCapability,
} from "./ownerJarvisCapabilityContract";

const actionPath = (action) => `/dashboard/owner?jarvisAction=${encodeURIComponent(action)}`;

const ACTION_DEFINITIONS = [
  ["finance", { label: "movimiento", formId: "finance", aliases: ["movimiento", "gasto", "ingreso", "transferencia", "registrar plata", "registrar dinero", "compra", "anotar un gasto", "anotar gasto", "cargar un gasto", "cargar gasto", "anotar un ingreso", "cargar un ingreso", "registrar un gasto", "registrar un ingreso"], ack: "Registrar movimiento.", path: actionPath("finance") }],
  ["consumption", { label: "consumo", formId: "finance", aliases: ["consumo", "consumir stock", "usar stock", "descontar stock"], ack: "Registrar consumo de stock.", path: actionPath("consumption") }],
  ["client", { label: "persona", formId: "client", resetContext: true, aliases: ["persona nueva", "cliente nuevo", "prospecto nuevo", "nuevo cliente", "nueva persona", "cargar cliente"], ack: "Nueva persona.", path: actionPath("client") }],
  ["proposal", { label: "propuesta", formId: "proposal", aliases: ["crear propuesta", "nueva propuesta", "presupuesto", "cotizacion", "venta posible"], ack: "Nueva propuesta.", path: actionPath("proposal") }],
  ["commitment", { label: "tarea", formId: "commitment", aliases: ["crear tarea", "nueva tarea", "seguimiento", "recordatorio", "turno", "agendar", "pendiente"], ack: "Nuevo seguimiento.", path: actionPath("commitment") }],
  ["routine", { label: "rutina", formId: "routine", aliases: ["crear rutina", "nueva rutina", "crear habito", "nuevo habito"], ack: "Nueva rutina.", path: actionPath("routine") }],
  ["worklog", { label: "registrar trabajo", formId: "worklog", aliases: ["registrar lo que hice", "registrar trabajo", "cargar lo que hice", "trabajo real"], ack: "Contame qué hiciste.", path: actionPath("worklog") }],
  ["recurring", { label: "recurrente", formId: "recurring", aliases: ["crear recurrente", "acuerdo recurrente", "venta mensual", "servicio mensual", "cobro mensual"], ack: "Nuevo acuerdo recurrente.", path: actionPath("recurring") }],
  ["interaction", { label: "nota", formId: "interaction", aliases: ["registrar contacto", "anotacion de cliente", "conversacion con cliente"], ack: "Registrar contacto.", path: actionPath("interaction") }],
  ["initiative", { label: "proyecto", formId: "initiative", aliases: ["crear proyecto", "nuevo proyecto", "crear iniciativa"], ack: "Nuevo proyecto.", path: actionPath("initiative") }],
  ["import", { label: "importar", formId: "import", aliases: ["importar movimientos", "importar gastos", "importar archivo"], ack: "Abro la importación.", path: actionPath("import") }],
  ["inboxProcess", { label: "procesar Inbox", aliases: ["procesar inbox", "vaciar inbox", "revisar inbox", "ordenar inbox"], contexts: ["hoy", "owner", "inbox"], ack: "Abro el procesador del Inbox.", path: actionPath("inboxProcess") }],
  ["today", { label: "hoy", route: "/dashboard/owner", aliases: ["inicio", "panel de hoy", "volver a hoy", "mi dia", "mi día"], contexts: ["hoy", "owner"], ack: "Voy a Hoy.", path: "/dashboard/owner" }],
  ["calendar", { label: "calendario", route: "/dashboard/owner/calendario", aliases: ["calendario", "agenda", "ver agenda", "ver calendario"], contexts: ["calendario"], ack: "Voy al calendario.", path: "/dashboard/owner/calendario" }],
  ["clients", { label: "clientes", route: "/dashboard/owner/clientes", aliases: ["ver clientes", "panel de clientes", "crm"], contexts: ["clientes"], ack: "Voy a clientes.", path: "/dashboard/owner/clientes" }],
  ["projectsPage", { label: "proyectos", route: "/dashboard/owner/proyectos", aliases: ["ver proyectos", "mis proyectos", "panel de proyectos", "proyectos 360"], contexts: ["proyectos"], ack: "Voy a proyectos.", path: "/dashboard/owner/proyectos" }],
  ["movements", { label: "movimientos", route: "/dashboard/owner/movimientos", aliases: ["ver movimientos", "tabla de movimientos"], contexts: ["movimientos", "finanzas"], ack: "Voy a movimientos.", path: "/dashboard/owner/movimientos" }],
  ["operation", { label: "operación", route: "/dashboard/owner/operacion", aliases: ["operacion", "ver operacion", "operativa"], contexts: ["operacion"], ack: "Voy a Operación.", path: "/dashboard/owner/operacion" }],
  ["account", { label: "caja", aliases: ["crear caja", "nueva caja", "crear cuenta", "nueva cuenta", "nuevo banco", "nueva billetera"], contexts: ["movimientos", "finanzas"], ack: "Nueva caja o cuenta.", path: "/dashboard/owner/movimientos?jarvisAction=account" }],
  ["category", { label: "categoría", aliases: ["crear categoria", "nueva categoria"], contexts: ["movimientos", "estadisticas", "finanzas"], ack: "Nueva categoría.", path: "/dashboard/owner/movimientos?jarvisAction=category" }],
  ["financePage", { label: "finanzas", route: "/dashboard/owner/finanzas", aliases: ["ver finanzas", "mis finanzas"], contexts: ["finanzas", "movimientos"], ack: "Voy a finanzas.", path: "/dashboard/owner/finanzas" }],
  ["stats", { label: "estadísticas", route: "/dashboard/owner/estadisticas", aliases: ["estadisticas", "estadistica", "futuro", "historico"], contexts: ["estadisticas", "finanzas"], ack: "Voy a estadísticas.", path: "/dashboard/owner/estadisticas" }],
];

const ACTION_DOMAINS = Object.freeze({
  finance: "finance",
  consumption: "inventory",
  client: "people",
  proposal: "commercial",
  commitment: "planning",
  routine: "routines",
  worklog: "time",
  recurring: "commercial",
  interaction: "people",
  initiative: "projects",
  import: "finance",
  inboxProcess: "memory",
  account: "finance",
  category: "finance",
});


const CONVERSATIONAL_NAVIGATION = Object.freeze({
  projectsPage: { phrases: ["ver proyectos", "abrir proyectos", "ir a proyectos", "panel de proyectos", "mis proyectos"], message: "Abro Proyectos." },
  movements: { phrases: ["ver movimientos", "abrir movimientos", "ir a movimientos", "tabla de movimientos"], message: "Abro Movimientos." },
  financePage: { phrases: ["ver finanzas", "abrir finanzas", "ir a finanzas", "mis finanzas"], message: "Abro Finanzas." },
  today: { phrases: ["ir a hoy", "abrir hoy", "volver a hoy", "panel de hoy"], message: "Vuelvo a Hoy." },
  inboxProcess: { phrases: ["procesar inbox", "vaciar inbox", "revisar inbox", "ordenar inbox"], message: "Abro el procesador del Inbox." },
});

const SURFACE_CONFIRMED_ACTIONS = new Set([
  "finance",
  "consumption",
  "client",
  "proposal",
  "commitment",
  "routine",
  "worklog",
  "recurring",
  "interaction",
  "initiative",
  "import",
  "account",
  "category",
]);

export function createOwnerJarvisVoiceActionCapabilities({ navigate }) {
  return ACTION_DEFINITIONS.map(([key, definition]) => {
    const { path, ...voice } = definition;
    const confirmation = SURFACE_CONFIRMED_ACTIONS.has(key)
      ? OWNER_JARVIS_CONFIRMATION.SURFACE
      : OWNER_JARVIS_CONFIRMATION.NEVER;
    const conversational = CONVERSATIONAL_NAVIGATION[key] || null;
    return defineOwnerJarvisCapability({
      id: `voice-action:${key}`,
      domain: ACTION_DOMAINS[key] || "navigation",
      description: `Acción de UI existente: ${definition.label || key}.`,
      reads: [],
      identify: conversational
        ? ({ text }) => conversational.phrases.some((phrase) => text.includes(phrase)) ? { operationId: key } : null
        : null,
      operations: [{
        id: key,
        kind: OWNER_JARVIS_OPERATION_KIND.PROPOSE,
        confirmation,
        voice: { key, ...voice },
        execute: () => {
          navigate(path);
          return { handled: true, message: conversational?.message || definition.ack || `Abro ${definition.label || key}.` };
        },
        errors: { execute: `No pude abrir ${definition.label || key} ahora.` },
      }],
      errors: { execute: `No pude abrir ${definition.label || key} ahora.` },
    });
  });
}

// Compatibilidad para cualquier consumidor anterior a MEJ-061.
export function createOwnerJarvisVoiceActions({ navigate }) {
  const entries = createOwnerJarvisVoiceActionCapabilities({ navigate }).map((capability) => {
    const operation = capability.operations[0];
    const { key, ...voice } = operation.voice;
    return [key, { ...voice, run: () => { operation.execute({}); } }];
  });
  return Object.fromEntries(entries);
}
